import { NgxSliderModule, Options } from '@angular-slider/ngx-slider';
import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { GoogleMap, GoogleMapsModule, MapCircle, MapGeocoder, MapMarker } from '@angular/google-maps';
import { PlaceTypeEnum } from '@buspro/shared/common';
import { CircleMarkerDto, MapMarkerDto } from '@buspro/shared/dtos';
import { AddStandardPlaceDto, StandardPlacesApi } from '@buspro/web/apis';
import {
  DEFAULT_PLACE_PHOTO,
  ErrorDisplayComponent,
  ExtendedProblemDetail,
  WindowService,
  useFormErrorHandler,
} from '@buspro/web/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';
import { PlaceAutocompleteComponent } from './place-autocomplete/place-autocomplete.component';

@Component({
  selector: 'buspro-admin-add-standard-place',
  standalone: true,
  imports: [
    NgIf,
    NzColDirective,
    NzDrawerModule,
    NzFormControlComponent,
    NzFormDirective,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzRadioComponent,
    NzRadioGroupComponent,
    NzRowDirective,
    NzTooltipDirective,
    ReactiveFormsModule,
    NzButtonModule,
    GoogleMapsModule,
    NzInputDirective,
    NzAutocompleteModule,
    PlaceAutocompleteComponent,
    MapCircle,
    MapMarker,
    ErrorDisplayComponent,
    NgxSliderModule,
  ],
  template: `
    <nz-drawer
      (nzOnClose)="hide.emit(true)"
      [nzClosable]="true"
      [nzTitle]="drawerTitleTpl"
      [nzVisible]="show"
      nzSize="large"
      [nzWidth]="1500"
    >
      <ng-template #closeIconTpl>
        <div class="mt-4">
          <span class="fa fa-close"></span>
        </div>
      </ng-template>
      <ng-template #drawerTitleTpl>
        <span class="fe fe-plus font-semibold"></span> New Place
      </ng-template>
      <form *nzDrawerContent [formGroup]="form" nz-form nzLayout="vertical" class="h-full">
        <div class="grid grid-cols-12 gap-6 h-full">
          <!-- Left column - Controls -->
          <div class="col-span-4">
            <app-error-display *ngIf="showError() && errorDetails" [error]="errorDetails" (dismiss)="onDismissError()" (retry)="add()" class="mb-4"></app-error-display>

            <div class="mb-4">
              <nz-form-item>
                <nz-form-control>
                  <buspro-admin-place-autocomplete
                    placeholder="Search for bus stations, subways, transits, taxi stands and parkings"
                    [options]="placeOptions"
                    (placeSelected)="handleAddressChange($event)"
                    (inputChanged)="onInputChanged($event)"
                    class="w-full"
                  />
                </nz-form-control>
              </nz-form-item>
            </div>

            <div>
              <nz-form-item>
                <nz-form-label [nzRequired]="true">Place Name</nz-form-label>
                <nz-form-control>
                  <input
                    formControlName="name"
                    nz-input
                    class="input w-full"
                    placeholder="Display name for this place"
                  />
                </nz-form-control>
              </nz-form-item>

              <nz-form-item>
                <nz-form-label [nzRequired]="true"
                  >Select Place Type</nz-form-label
                >
                <nz-form-control>
                  <nz-radio-group formControlName="placeType">
                    <label
                      [nzValue]="terminalTypesEnum.Terminal"
                      nz-radio
                      nz-tooltip
                      nzTooltipTitle="Where a route starts or ends. Terminals can also be used as intermediate stops in routes."
                      >Terminal</label
                    >
                    <label
                      [nzValue]="terminalTypesEnum.BusStop"
                      nz-radio
                      nz-tooltip
                      nzTooltipTitle="These are places where buses pass but do not end or start trips from"
                      >Bus Stop</label
                    >
                  </nz-radio-group>
                </nz-form-control>
              </nz-form-item>

              <nz-form-item>
                <nz-form-label [nzRequired]="true"
                  >Geofence radius (meters)</nz-form-label
                >
                <nz-form-control>
                  <!-- Preset buttons -->
                  <div class="flex flex-wrap gap-2 mb-3" *ngIf="selectedGeolocation()">
                    <button
                      type="button"
                      (click)="applyPreset(PRESETS.busStop)"
                      class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      [class.bg-blue-100]="radius?.value === PRESETS.busStop"
                    >
                      Bus Stop (800m)
                    </button>
                    <button
                      type="button"
                      (click)="applyPreset(PRESETS.terminal)"
                      class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      [class.bg-blue-100]="radius?.value === PRESETS.terminal"
                    >
                      Terminal (1500m)
                    </button>
                    <button
                      type="button"
                      (click)="applyPreset(PRESETS.busStop2x)"
                      class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      [class.bg-blue-100]="radius?.value === PRESETS.busStop2x"
                    >
                      2x Bus Stop (1600m)
                    </button>
                    <button
                      type="button"
                      (click)="applyPreset(PRESETS.terminal2x)"
                      class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      [class.bg-blue-100]="radius?.value === PRESETS.terminal2x"
                    >
                      2x Terminal (3000m)
                    </button>
                    <button
                      type="button"
                      (click)="applyPreset(PRESETS.busStop3x)"
                      class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      [class.bg-blue-100]="radius?.value === PRESETS.busStop3x"
                    >
                      3x Bus Stop (2400m)
                    </button>
                    <button
                      type="button"
                      (click)="applyPreset(PRESETS.terminal3x)"
                      class="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      [class.bg-blue-100]="radius?.value === PRESETS.terminal3x"
                    >
                      3x Terminal (4500m)
                    </button>
                  </div>
                  <ngx-slider
                    [value]="radius?.value || 150"
                    [options]="sliderOptions"
                    (valueChange)="onRadiusChange($event)"
                  ></ngx-slider>
                  <div
                    *ngIf="!selectedGeolocation()"
                    class="text-xs text-gray-500 mt-2"
                  >
                    Please search and select a place first to adjust the radius
                  </div>
                  <div *ngIf="selectedGeolocation()" class="text-xs text-gray-500 mt-2">
                    Current: {{ radius?.value || 150 }}m
                    <span *ngIf="(radius?.value || 0) >= 1000">
                      ({{ ((radius?.value || 0) / 1000).toFixed(1) }} km)
                    </span>
                  </div>
                </nz-form-control>
              </nz-form-item>
            </div>

            <!-- Action buttons -->
            <div class="flex justify-end space-x-2 mt-10">
              <button
                (click)="hide.emit(true)"
                class="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                (click)="add()"
                [disabled]="isAddButtonDisabled"
                [class.loading]="mutation.isPending()"
                class="btn btn-primary"
              >
                Add
              </button>
            </div>
          </div>

          <!-- Right column - Map -->
          <div class="col-span-8 h-full">
            <div class="relative h-full">
              <google-map
                #map
                height="100%"
                width="100%"
                [options]="mapOptions"
                [center]="center"
                [zoom]="zoom"
                (mapClick)="onMapClicked($event)"
                (mapMousemove)="move($event)"
              >
                <map-circle
                  #placeCircle
                  [center]="circle().center!"
                  [radius]="circle().radius!"
                  [options]="{
                    fillColor: circle().fillColor || '#4285F4',
                    fillOpacity: 0.2,
                    strokeColor: circle().strokeColor || '#4285F4',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    editable: false,
                    draggable: true,
                    clickable: true,
                    zIndex: 1
                  }"
                  (radiusChanged)="onCircleRadiusChange()"
                  (circleDragend)="onCircleDragend($event)"
                />

                <map-marker
                  [options]="marker().options"
                  [label]="name!.value!"
                  [position]="marker().position"
                  [title]="marker().title"
                />
              </google-map>

              <!-- Custom radius control panel -->
              <div
                class="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg z-10"
              >
                <div class="flex flex-col">
                  <h3 class="text-lg font-medium mb-2">Adjust Radius</h3>
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm">Fine adjust:</span>
                    <div class="flex items-center space-x-2">
                      <button
                        class="btn btn-xs btn-circle"
                        (click)="adjustRadius(-50)"
                        [disabled]="(radius?.value || 0) <= 100"
                      >
                        <span class="fe fe-minus"></span>
                      </button>
                      <button
                        class="btn btn-xs btn-circle"
                        (click)="adjustRadius(50)"
                      >
                        <span class="fe fe-plus"></span>
                      </button>
                    </div>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm">Coarse adjust:</span>
                    <div class="flex items-center space-x-2">
                      <button
                        class="btn btn-xs btn-circle"
                        (click)="adjustRadius(-500)"
                        [disabled]="(radius?.value || 0) <= 550"
                      >
                        <span class="fe fe-minus"></span>
                      </button>
                      <button
                        class="btn btn-xs btn-circle"
                        (click)="adjustRadius(500)"
                      >
                        <span class="fe fe-plus"></span>
                      </button>
                    </div>
                  </div>
                  <div class="mt-2 text-center font-medium">
                    {{ radius?.value || 0 }} meters
                  </div>
                  <div class="mt-1 text-xs text-gray-500 text-center">
                    Current radius
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </nz-drawer>
  `,
  styles: `
    :host ::ng-deep .ant-drawer-body {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    :host ::ng-deep .ant-drawer-content-wrapper {
      display: flex;
      flex-direction: column;
    }
  `,
})
export class AddStandardPlaceComponent implements OnInit, AfterViewInit {
  private formBuilder = inject(FormBuilder);

  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  private standardPlacesApi = inject(StandardPlacesApi);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private geocoder = inject(MapGeocoder);
  private global = inject(WindowService);

  // Flag to prevent circular updates
  private updatingCircle = false;

  protected readonly terminalTypesEnum = PlaceTypeEnum;

  @Input() show = false;
  @Input({ required: true }) defaultPlaceType = PlaceTypeEnum.Terminal;
  @Output() hide = new EventEmitter<boolean>();
  @Output() refresh = new EventEmitter<void>();
  selectedGeolocation = signal<google.maps.places.PlaceResult | null>(null);
  // Removed ViewChild for deprecated autocomplete directive
  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild(PlaceAutocompleteComponent, { static: false }) placeAutocomplete!: PlaceAutocompleteComponent;

  @ViewChild(MapCircle, { static: false }) placeCircle!: MapCircle;

  center: google.maps.LatLngLiteral = { lat: 24, lng: 12 };
  zoom = 12;
  display!: google.maps.LatLngLiteral;
  marker = signal<MapMarkerDto>(new MapMarkerDto());
  circle = signal<CircleMarkerDto>(new CircleMarkerDto());
  placeOptions = {
    componentRestrictions: { country: ['GH', 'UG'] },
    strictBounds: true,
  };

  // Preset radius values (accessible in template)
  // Must be defined before form initialization to avoid undefined access
  readonly PRESETS = {
    busStop: 800,
    terminal: 1500,
    busStop2x: 1600,
    terminal2x: 3000,
    busStop3x: 2400,
    terminal3x: 4500,
  } as const;

  // Get default radius based on place type
  private getDefaultRadiusForPlaceType(placeType: PlaceTypeEnum): number {
    switch (placeType) {
      case PlaceTypeEnum.BusStop:
        return this.PRESETS.busStop;
      case PlaceTypeEnum.Terminal:
      case PlaceTypeEnum.TerminalBusStop:
        return this.PRESETS.terminal;
      default:
        return this.PRESETS.terminal;
    }
  }

  form = this.formBuilder.group({
    name: ['', [Validators.required]],
    placeId: ['', [Validators.required]],
    lat: [0, [Validators.required, Validators.min(-90), Validators.max(90)]],
    lng: [0, [Validators.required, Validators.min(-180), Validators.max(180)]],
    placeType: [this.defaultPlaceType, [Validators.required]],
    radius: [this.getDefaultRadiusForPlaceType(this.defaultPlaceType), [Validators.required, Validators.min(150)]],
    country: ['', [Validators.required]],
    photoUrl: [DEFAULT_PLACE_PHOTO, [Validators.required]],
  });

  name = this.form.get('name');
  radius = this.form.get('radius');
  lat = this.form.get('lat');
  lng = this.form.get('lng');
  placeId = this.form.get('placeId');
  placeType = this.form.get('placeType');
  country = this.form.get('country');
  photoUrl = this.form.get('photoUrl');

  // Get dynamic step based on current radius value
  private getStepForRadius(radius: number): number {
    if (radius < 1000) {
      return 10; // 10m steps for radii < 1km
    } else if (radius < 10000) {
      return 50; // 50m steps for radii 1km-10km
    } else {
      return 100; // 100m steps for radii > 10km
    }
  }

  // Configuration for ngx-slider
  sliderOptions: Options = {
    floor: 150,
    ceil: 100000,
    step: 10,
    showTicks: false,
    showTicksValues: false,
    animate: true,
    showSelectionBar: true,
    getSelectionBarColor: (_value: number): string => {
      return this.selectedGeolocation() ? '#2563eb' : '#9ca3af'; // Blue when active, gray when disabled
    },
    getPointerColor: (_value: number): string => {
      return this.selectedGeolocation() ? '#2563eb' : '#9ca3af'; // Blue when active, gray when disabled
    },
    translate: (value: number): string => {
      return value + ' m';
    },
    disabled: true, // Default disabled until a location is selected
  };

  ngAfterViewInit(): void {
    this.logger.log('ngAfterViewInit - component initialized');

    // Check if we already have an address selected and make sure the circle is rendered
    if (this.selectedGeolocation() && this.circle().center) {
      this.logger.log('Address already selected, ensuring circle visibility');
      // Use a small delay to allow Angular to complete change detection and rendering
      this.global.nativeWindow.setTimeout(() => this.ensureCircleVisibility(), 100);
    }

    // Debug autocomplete component status
    this.global.nativeWindow.setTimeout(() => {
      if (this.placeAutocomplete) {
        this.logger.log('PlaceAutocomplete component available:', this.placeAutocomplete);
      } else {
        this.logger.warn('PlaceAutocomplete component not available');
      }
    }, 1000);
  }

  mutation = injectMutation(() => ({
    mutationFn: (payload: AddStandardPlaceDto) =>
      lastValueFrom(
        this.standardPlacesApi.standardPlaceControllerAddStandardPlace({
          body: payload,
        }),
      ),
    onSuccess: () => {
      this.clearErrors();
      this.hide.emit(true);
      this.refresh.emit();
      this.form.reset();
      this.selectedGeolocation.set(null);
      this.circle.set(new CircleMarkerDto()); // Reset circle
    },
    onError: (error: any) => {
      this.logger.error('Error adding standard place', error);
      const result = this.formErrorHandler.handleFormError(error, { formName: 'admin-add-standard-place-form' });
      this.errorDetails = result.problemDetail ?? null;
      this.showError.set(!!this.errorDetails);
    },
  }));
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
  };

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  onMapClicked(event: google.maps.MapMouseEvent) {
    if (event.latLng) {
      const latLng = event.latLng.toJSON();
      this.getPlaceId(latLng);
      this.map.panTo(latLng);
    } else {
      this.logger.warn('Map clicked event does not contain valid latLng data');
      this.notification.error(
        'Unable to determine the location. Please try selecting a different point on the map.',
        '',
      );
    }
  }

  move(event: google.maps.MapMouseEvent) {
    if (event.latLng) {
      this.display = event.latLng.toJSON();
    } else {
      this.logger.warn('Map move event does not contain valid latLng data');
      this.notification.error(
        'Unable to determine the location. Please try selecting a different point on the map.',
        '',
      );
    }
  }

  ngOnInit(): void {
    this.global.nativeWindow.navigator.geolocation.getCurrentPosition(
      (position: { coords: { latitude: number; longitude: number } }) => {
        this.center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
      },
    );

    // Update default radius when placeType changes
    this.placeType?.valueChanges.subscribe((placeType) => {
      if (placeType) {
        const defaultRadius = this.getDefaultRadiusForPlaceType(placeType);
        // Always update to the default radius for the selected place type
        this.radius?.setValue(defaultRadius);
      }
    });

    this.radius?.valueChanges.subscribe((val) => {
      if (val) {
        // Update slider step based on current radius
        this.sliderOptions = {
          ...this.sliderOptions,
          step: this.getStepForRadius(val),
        };

        // Update the circle with the new radius
        this.circle.update((current) => {
          current.radius = val;
          return current;
        });

        // Fit the map to the new circle size
        this.ensureMapLibrariesLoaded().then(() => {
          this.global.nativeWindow.setTimeout(() => this.fitCircleInView(), 100);
        });
      }
    });
  }

  /**
   * Apply a preset radius value
   */
  applyPreset(presetValue: number): void {
    if (this.radius) {
      this.radius.setValue(presetValue);
    }
  }

  /**
   * Helper method to ensure Google Maps libraries are loaded
   * This is particularly important for the geometry library which is needed for circle calculations
   */
  private ensureMapLibrariesLoaded(): Promise<boolean> {
    return new Promise((resolve) => {
      // Check if geometry library is already loaded
      if (google.maps.geometry && google.maps.geometry.spherical) {
        resolve(true);
        return;
      }

      // If not loaded, wait a bit and check again
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        if (google.maps.geometry && google.maps.geometry.spherical) {
          clearInterval(checkInterval);
          this.logger.log('Google Maps geometry library loaded successfully');
          resolve(true);
        } else if (attempts >= 10) {
          // Give up after 10 attempts (5 seconds)
          clearInterval(checkInterval);
          this.logger.warn('Google Maps geometry library not available, using fallback');
          resolve(false);
        }
      }, 500);
    });
  }

  add() {
    // Clear any previous errors
    this.clearErrors();

    // Validate that a place has been selected
    if (!this.selectedGeolocation()) {
      this.notification.warning('No place selected', 'Please search and select a place before adding.');
      return;
    }

    // Validate form values
    if (this.form.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        control?.markAsTouched();
        control?.markAsDirty();
      });

      this.notification.warning('Invalid form', 'Please complete all required fields correctly.');
      return;
    }

    const payload: AddStandardPlaceDto = {
      name: this.name!.value!,
      googleMapPlaceId: this.form.get('placeId')!.value!,
      lng: this.form.get('lng')!.value!,
      lat: this.form.get('lat')!.value!,
      radius: this.radius!.value!,
      type: this.form.get('placeType')!.value!,
      country: this.country!.value!,
    };

    this.logger.log('add standard place payload', payload);

    // Submit the form using the mutation
    this.mutation.mutate(payload);
  }

  onInputChanged(value: string) {
    this.logger.log('🔍 onInputChanged called with value:', value);
    // Clear the form when user starts typing a new search
    if (value && value !== this.name?.value) {
      this.logger.log('🔍 Clearing form data due to new search input');
      this.clearFormData();
    }
  }

  private clearFormData() {
    this.selectedGeolocation.set(null);
    this.name?.setValue('');
    this.placeId?.setValue('');
    this.lat?.setValue(0);
    this.lng?.setValue(0);
    this.country?.setValue('');
    this.photoUrl?.setValue(DEFAULT_PLACE_PHOTO);
    this.marker.set(new MapMarkerDto());
    this.circle.set(new CircleMarkerDto());

    // Reset form validation state
    this.name?.markAsUntouched();
    this.name?.markAsPristine();
    this.placeId?.markAsUntouched();
    this.placeId?.markAsPristine();
    this.lat?.markAsUntouched();
    this.lat?.markAsPristine();
    this.lng?.markAsUntouched();
    this.lng?.markAsPristine();
    this.country?.markAsUntouched();
    this.country?.markAsPristine();
    this.photoUrl?.markAsUntouched();
    this.photoUrl?.markAsPristine();

    // Update form validation status
    this.form.updateValueAndValidity();

    // Disable slider until new place is selected
    this.sliderOptions = {
      ...this.sliderOptions,
      disabled: true,
    };
  }

  handleAddressChange(address: any) {
    this.logger.log('🔍 handleAddressChange called with:', address);
    this.logger.log('🔍 Address type:', typeof address);
    this.logger.log('🔍 Address keys:', address ? Object.keys(address) : 'null');

    // Handle null/empty address
    if (!address) {
      this.logger.log('❌ Address is null/empty, clearing form data');
      this.clearFormData();
      return;
    }

    this.selectedGeolocation.set(address);
    this.logger.log('Selected geolocation set:', address);

    // Enable the slider by updating options
    this.sliderOptions = {
      ...this.sliderOptions,
      disabled: false,
    };

    const photoUrl =
      address.photos?.[0]?.getUrl({
        maxWidth: 500,
        maxHeight: 500,
      }) ?? null;

    const lat = address.geometry?.location?.lat();
    const lng = address.geometry?.location?.lng();

    const country = address.address_components?.find((component: any) =>
      component.types.includes('country'),
    )?.long_name;

    // Validate required location data
    if (lat === undefined || lng === undefined) {
      this.logger.error('Location data is undefined', { address, lat, lng });
      this.notification.error('Could not determine the location coordinates', '');
      return;
    }

    this.logger.log('Location data extracted:', { lat, lng, country });

    // set the form values
    this.logger.log('🔍 Setting form values:', {
      name: address.name,
      placeId: address.place_id,
      lat: lat,
      lng: lng,
      country: country,
      photoUrl: photoUrl,
    });

    this.logger.log('🔍 Address object structure:', {
      hasPlaceId: 'place_id' in address,
      placeIdValue: address.place_id,
      addressKeys: Object.keys(address),
    });

    this.name?.setValue(address.name ?? null);

    // Handle placeId - use address.place_id or generate a fallback
    const placeIdValue = address.place_id || `manual_${Date.now()}_${lat}_${lng}`;
    this.logger.log('🔍 Setting placeId:', placeIdValue);
    this.placeId?.setValue(placeIdValue);

    this.lat?.setValue(lat);
    this.lng?.setValue(lng);
    this.country?.setValue(country ?? null);
    this.photoUrl?.setValue(photoUrl ?? DEFAULT_PLACE_PHOTO);

    this.logger.log('🔍 Form values after setting:', {
      name: this.name?.value,
      placeId: this.placeId?.value,
      lat: this.lat?.value,
      lng: this.lng?.value,
      country: this.country?.value,
      photoUrl: this.photoUrl?.value,
    });

    // Mark form controls as touched and dirty to trigger validation
    this.name?.markAsTouched();
    this.name?.markAsDirty();
    this.placeId?.markAsTouched();
    this.placeId?.markAsDirty();
    this.lat?.markAsTouched();
    this.lat?.markAsDirty();
    this.lng?.markAsTouched();
    this.lng?.markAsDirty();
    this.country?.markAsTouched();
    this.country?.markAsDirty();
    this.photoUrl?.markAsTouched();
    this.photoUrl?.markAsDirty();

    // Update form validation status with a delay to ensure all controls are set
    setTimeout(() => {
      this.logger.log('🔍 Form values before validation:', {
        name: this.name?.value,
        placeId: this.placeId?.value,
        lat: this.lat?.value,
        lng: this.lng?.value,
        country: this.country?.value,
        placeType: this.placeType?.value,
        radius: this.radius?.value,
      });

      this.form.updateValueAndValidity();

      // Debug form validation status
      this.logger.log('🔍 Form validation status after place selection:', {
        valid: this.form.valid,
        invalid: this.form.invalid,
        errors: this.form.errors,
        name: this.name?.value,
        placeId: this.placeId?.value,
        lat: this.lat?.value,
        lng: this.lng?.value,
        country: this.country?.value,
        placeType: this.placeType?.value,
        radius: this.radius?.value,
        nameErrors: this.name?.errors,
        placeIdErrors: this.placeId?.errors,
        latErrors: this.lat?.errors,
        lngErrors: this.lng?.errors,
        countryErrors: this.country?.errors,
        placeTypeErrors: this.placeType?.errors,
        radiusErrors: this.radius?.errors,
      });

      // Log validation status
      if (this.form.invalid) {
        this.logger.log('❌ Form still invalid after place selection');
      } else {
        this.logger.log('✅ Form is now valid!');
      }
    }, 200); // Increased delay to ensure all values are set

    // set the map center
    this.center = {
      lat: lat,
      lng: lng,
    };

    // Update map view
    if (this.map?.googleMap) {
      this.map.googleMap.panTo(this.center);
    }

    // Update radius to smart default based on current placeType when place is selected
    // This ensures the radius matches the place type (800m for BusStop, 1500m for Terminal)
    const placeType = this.placeType?.value || this.defaultPlaceType;
    const smartDefaultRadius = this.getDefaultRadiusForPlaceType(placeType);
    const currentRadius = this.radius?.value || 0;

    // If radius is still at initial default or hasn't been customized, update to smart default
    // Check if it's the initial default (from form initialization) or a generic value like 2000
    const initialDefault = this.getDefaultRadiusForPlaceType(this.defaultPlaceType);
    // Update if: radius is 0, matches initial default, or is the old hardcoded 2000
    if (currentRadius === 0 || currentRadius === initialDefault || currentRadius === 2000) {
      this.radius?.setValue(smartDefaultRadius);
    }

    // Ensure marker is updated before circle
    this.updateMarker(address);

    // Update the circle (which will handle fit to view)
    this.updateCircle(address);

    this.logger.log('form', this.form.getRawValue());
  }

  private updateMarker(address: any) {
    this.marker.set({
      id: address.place_id!,
      position: {
        lat: address.geometry!.location!.lat()!,
        lng: address.geometry!.location!.lng()!,
      },
      label: {
        color: 'green',
        text: 'Marker label ' + (this.marker.length + 1),
      },
      title: 'Marker title ' + (this.marker.length + 1),
      info: 'Marker info ' + (this.marker.length + 1),
      options: {
        animation: google.maps.Animation.BOUNCE,
      },
    });
  }

  private updateCircle(address: any) {
    // Use the current radius value from the form (which should already be set to smart default)
    const radiusValue =
      this.radius!.value || this.getDefaultRadiusForPlaceType(this.placeType?.value || this.defaultPlaceType);

    this.circle.set({
      center: {
        lat: address.geometry!.location!.lat()!,
        lng: address.geometry!.location!.lng()!,
      },
      id: address.place_id!,
      name: address.name!,
      fillColor: '#4285F4',
      type: '',
      radius: radiusValue,
      routeId: null,
      draggable: true,
      editable: false,
      strokeColor: '#4285F4',
      fillOpacity: 0.2,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      zIndex: 1,
      clickable: true,
    });

    // Ensure radius form control is updated to match
    this.radius?.setValue(radiusValue);

    // Ensure all libraries are loaded before proceeding
    this.ensureMapLibrariesLoaded().then(() => {
      // More reliable approach: use requestAnimationFrame for timing
      requestAnimationFrame(() => {
        // Double-check that circle center is set correctly
        if (!this.circle().center) {
          this.logger.warn('Circle center not set properly, fixing...');
          this.circle.update((current) => {
            current.center = {
              lat: address.geometry!.location!.lat()!,
              lng: address.geometry!.location!.lng()!,
            };
            return current;
          });
        }

        // Ensure circle is visible with a retry mechanism
        this.ensureCircleVisibility();
      });
    });
  }

  /**
   * Ensures the circle is visible on the map with a retry mechanism
   * This helps resolve timing issues with Google Maps components
   */
  private ensureCircleVisibility(retryCount = 0): void {
    // Maximum number of retries
    const maxRetries = 3;

    // Check if circle is properly initialized
    if (this.placeCircle && this.circle().center && this.circle().radius) {
      // Circle appears to be configured properly, fit it to view
      this.fitCircleInView();

      // Force a check after a short delay to ensure it's really visible
      setTimeout(() => {
        if (this.placeCircle) {
          // Log success for debugging
          this.logger.log('Circle successfully rendered');
        } else if (retryCount < maxRetries) {
          // Try again if we haven't exceeded max retries
          this.logger.warn(`Circle not visible, retrying (${retryCount + 1}/${maxRetries})...`);
          this.ensureCircleVisibility(retryCount + 1);
        }
      }, 300);
    } else if (retryCount < maxRetries) {
      // Circle not ready yet, retry after a delay
      this.logger.warn(`Circle not ready, retrying (${retryCount + 1}/${maxRetries})...`);
      setTimeout(() => this.ensureCircleVisibility(retryCount + 1), 300);
    } else {
      // Give up after max retries
      this.logger.error('Failed to render circle after maximum retries');
      this.notification.warning(
        'The map location has been set, but the radius visualization may not be displaying correctly. You can still adjust the radius using the controls.',
        '',
      );
    }
  }

  onCircleRadiusChange() {
    // Prevent circular updates
    if (this.updatingCircle) {
      return;
    }

    this.updatingCircle = true;

    if (this.placeCircle && this.radius) {
      const newRadius = this.placeCircle.getRadius();

      // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.radius?.setValue(newRadius, { emitEvent: false });

        // Fit the map to the new circle size
        setTimeout(() => {
          this.fitCircleInView();
          // Reset the flag after updates are complete
          setTimeout(() => {
            this.updatingCircle = false;
          }, 100);
        }, 0);
      }, 0);
    } else {
      this.logger.warn('PlaceCircle or radius form control is not initialized');
      this.updatingCircle = false;
    }
  }

  onCircleDragend($event: google.maps.MapMouseEvent) {
    if ($event.latLng) {
      const latlng = $event.latLng.toJSON();
      this.map.panTo(latlng);
      this.getPlaceId(latlng);
    } else {
      this.logger.warn('No valid latLng in the MapMouseEvent');
      this.errorDetails = {
        title: 'Location detection failed',
        detail: 'Unable to determine the location. Please try selecting a different point on the map.',
      } as any;
      this.showError.set(true);
    }
  }

  getPlaceId(latlng: { lat: number; lng: number }) {
    this.geocoder.geocode({ location: latlng }).subscribe((value) => {
      if (value.results.length > 0) {
        this.logger.log('geocoding result', value.results);
        this.handleAddressChange(value.results[0]);
      } else {
        this.logger.warn('No geocoding results found for the given location', latlng);
        this.notification.error('No geocoding results found for the given location.', '');
      }
    });
  }

  /**
   * Increase the zoom level of the map by 1
   */
  zoomIn() {
    if (this.zoom < 20) {
      this.zoom += 1;
      if (this.map?.googleMap) {
        this.map.googleMap.setZoom(this.zoom);
      }
    }
  }

  /**
   * Decrease the zoom level of the map by 1
   */
  zoomOut() {
    if (this.zoom > 1) {
      this.zoom -= 1;
      if (this.map?.googleMap) {
        this.map.googleMap.setZoom(this.zoom);
      }
    }
  }

  /**
   * Adjust the circle radius by the specified amount
   * This provides a safe way to adjust the circle size without triggering passive event listener warnings
   */
  adjustRadius(adjustment: number) {
    if (this.radius) {
      const currentValue = this.radius.value || 2000;
      const newValue = Math.max(50, Math.min(100000, currentValue + adjustment));

      console.log(`Adjusting radius from ${currentValue} to ${newValue}`);
      this.radius.setValue(newValue);
    }
  }

  /**
   * Fit the current circle in the map view
   * This ensures the entire circle is visible as radius changes
   */
  fitCircleInView(): void {
    // Ensure we have a map and circle to work with
    if (!this.map?.googleMap || !this.circle().center || !this.circle().radius) {
      this.logger.warn('Cannot fit circle to view: missing map, center, or radius');
      return;
    }

    try {
      const circleCenter = this.circle().center;
      const radius = this.circle().radius;

      // Create bounds that will fit the circle
      const bounds = new google.maps.LatLngBounds();

      // Add center point
      bounds.extend(new google.maps.LatLng(circleCenter.lat, circleCenter.lng));

      // Add 4 points on the circle edge in cardinal directions
      // Check if google.maps.geometry is available
      if (google.maps.geometry && google.maps.geometry.spherical) {
        const north = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(circleCenter.lat, circleCenter.lng),
          radius,
          0, // North
        );
        const east = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(circleCenter.lat, circleCenter.lng),
          radius,
          90, // East
        );
        const south = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(circleCenter.lat, circleCenter.lng),
          radius,
          180, // South
        );
        const west = google.maps.geometry.spherical.computeOffset(
          new google.maps.LatLng(circleCenter.lat, circleCenter.lng),
          radius,
          270, // West
        );

        bounds.extend(north);
        bounds.extend(east);
        bounds.extend(south);
        bounds.extend(west);
      } else {
        // Fallback if geometry library is not loaded
        // Add simple points based on rough lat/lng offsets
        const latOffset = radius / 111000; // Approx meters per degree latitude
        const lngOffset = radius / (111000 * Math.cos((circleCenter.lat * Math.PI) / 180));

        bounds.extend(new google.maps.LatLng(circleCenter.lat + latOffset, circleCenter.lng));
        bounds.extend(new google.maps.LatLng(circleCenter.lat - latOffset, circleCenter.lng));
        bounds.extend(new google.maps.LatLng(circleCenter.lat, circleCenter.lng + lngOffset));
        bounds.extend(new google.maps.LatLng(circleCenter.lat, circleCenter.lng - lngOffset));
      }

      // Apply the bounds with padding
      this.map.fitBounds(bounds, 50);

      // Log for debugging
      this.logger.log(`Adjusted map view to fit circle with radius: ${radius}m`);
    } catch (error) {
      this.logger.error('Error fitting circle to view', error);
    }
  }

  onRadiusChange(value: number) {
    // Prevent circular updates
    if (this.updatingCircle) {
      return;
    }

    this.updatingCircle = true;

    if (this.radius) {
      // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        // Update the form control
        this.radius?.setValue(value, { emitEvent: false });

        // Update the circle on the map
        if (this.circle) {
          this.circle.update((current) => {
            current.radius = value;
            return current;
          });
        }

        // Update the map view to fit the new circle
        setTimeout(() => {
          this.fitCircleInView();
          // Reset the flag after a delay to ensure all updates are complete
          setTimeout(() => {
            this.updatingCircle = false;
          }, 100);
        }, 0);
      }, 0);
    } else {
      this.updatingCircle = false;
    }
  }

  // Helper method to update circle radius by recreating the circle with the new radius
  private updateCircleRadius(radius: number) {
    // Prevent circular updates
    if (this.updatingCircle) {
      return;
    }

    this.updatingCircle = true;

    if (this.circle) {
      const currentCircle = this.circle();

      // If we have a center, recreate the circle with the new radius
      if (currentCircle.center) {
        this.circle.set({
          ...currentCircle,
          radius: radius,
        });
      }
    }

    // Reset the flag after a delay
    setTimeout(() => {
      this.updatingCircle = false;
    }, 100);
  }

  // Getter for button disabled state with debugging
  get isAddButtonDisabled(): boolean {
    const isDisabled = this.form.invalid || this.mutation.isPending();

    if (isDisabled) {
      // this.logger.log('Add button disabled because:', {
      //   formInvalid: this.form.invalid,
      //   mutationPending: this.mutation.isPending(),
      //   formErrors: this.form.errors,
      //   controlErrors: {
      //     name: this.name?.errors,
      //     placeId: this.placeId?.errors,
      //     lat: this.lat?.errors,
      //     lng: this.lng?.errors,
      //     country: this.country?.errors,
      //     placeType: this.placeType?.errors,
      //     radius: this.radius?.errors,
      //   },
      // });
    }

    return isDisabled;
  }
}
